import { CalendarClock, History } from "lucide-react";
import { cn } from "../lib/utils";
import { TONE_SOFT } from "./tones";

interface Props {
  /** When the record is about, "YYYY-MM-DD" (a meeting or evaluation date a user may have back-dated). */
  recordDate?: string | null;
  /** When the row was really created: a server timestamp, stored as naive UTC. null/"" = not tracked. */
  recordedAt?: string | null;
  className?: string;
}

/** The office works in Karachi, and "which day was this entered" is decided there. */
const OFFICE_TZ = "Asia/Karachi";

/** Server timestamps are naive UTC ("2026-10-29T09:04:15" / "2026-10-29 09:04:15"). */
function parseServerUtc(value: string): Date | null {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] ?? 0), Number(m[5] ?? 0), Number(m[6] ?? 0)));
}

/** Calendar day number of an instant in the office timezone. */
function officeDay(d: Date): number {
  const [y, m, day] = new Intl.DateTimeFormat("en-CA", { timeZone: OFFICE_TZ }).format(d).split("-").map(Number);
  return Math.round(Date.UTC(y, m - 1, day) / 86400000);
}

function plainDay(value: string): number | null {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? Math.round(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86400000) : null;
}

/**
 * "Logged on" stamp for records whose date can be chosen by the author.
 * Always shows the real creation date and time (office time); when that is
 * later than the record's own date it turns into a warning "Back-dated" pill
 * so an admin notices at a glance that the entry was made after the fact.
 */
export default function LoggedOn({ recordDate, recordedAt, className }: Props) {
  const recorded = recordedAt ? parseServerUtc(recordedAt) : null;
  if (!recorded) {
    return (
      <span className={cn("inline-flex items-center gap-1.5 text-xs text-muted-foreground", className)} title="This record was created before creation times were tracked.">
        <CalendarClock className="w-3.5 h-3.5" />
        Logged: not tracked
      </span>
    );
  }

  const recordDay = recordDate ? plainDay(recordDate) : null;
  const lagDays = recordDay == null ? 0 : officeDay(recorded) - recordDay;
  const stamp = new Intl.DateTimeFormat(undefined, {
    timeZone: OFFICE_TZ, year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
  }).format(recorded);

  if (lagDays >= 1) {
    return (
      <span
        className={cn("inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium", TONE_SOFT.warning, className)}
        title={`The date on this record is ${lagDays} day${lagDays === 1 ? "" : "s"} earlier than the day it was entered (Karachi time).`}
      >
        <History className="w-3.5 h-3.5" />
        Back-dated · logged {stamp} ({lagDays} day{lagDays === 1 ? "" : "s"} later)
      </span>
    );
  }

  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium", TONE_SOFT.neutral, className)} title="Karachi time">
      <CalendarClock className="w-3.5 h-3.5" />
      Logged {stamp}
    </span>
  );
}
